import { store } from "@/store";
import { UUID_SEGMENT_REGEX } from "@/constants/regex";

const cleanSuffix = (suffix: string) => {
  if (!suffix) return "";
  return suffix.startsWith("/") ? suffix : `/${suffix}`;
};

let mockStore: typeof store | null = null;

const getStore = () => {
  if (mockStore) return mockStore;
  try {
    return store;
  } catch (
    // In circular import scenarios (e.g., nav seeds during store init) we can safely return null
    _err
  ) {
    return null as unknown as typeof store;
  }
};

export const currentOrgUuid = (orgType?: string) => {
  const s = getStore();
  const selectedCustomer = s?.getters?.["authentication/selectedCustomer"];
  const selectedMsp = s?.getters?.["authentication/selectedMsp"];
  const selectedAccount = s?.getters?.["authentication/selectedAccount"];
  const routeOrgUuid =
    (() => {
      if (typeof location === "undefined") return null;
      const parts = location.pathname.split("/").filter(Boolean);
      if (
        parts.length >= 2 &&
        parts[0] === orgType &&
        UUID_SEGMENT_REGEX.test(parts[1])
      ) {
        return parts[1];
      }
      return null;
    })() || null;

  const selectedAccountType =
    selectedAccount?.type_name?.toLowerCase?.() ||
    selectedAccount?.type?.toLowerCase?.() ||
    null;

  if (orgType === "customer") {
    if (selectedCustomer?.uuid) return selectedCustomer.uuid;
    if (selectedAccountType === "customer" || !selectedAccountType)
      return selectedAccount?.uuid || null;
    if (routeOrgUuid) return routeOrgUuid as string;
    return null;
  }

  if (orgType === "msp") {
    if (selectedMsp?.uuid) return selectedMsp.uuid;
    if (selectedAccountType === "msp" || !selectedAccountType)
      return selectedAccount?.uuid || null;
    if (routeOrgUuid) return routeOrgUuid as string;
    return null;
  }

  if (orgType === "system") {
    if (selectedAccountType === "system" || !selectedAccountType)
      return selectedAccount?.uuid || null;
    if (routeOrgUuid) return routeOrgUuid as string;
  }

  return (
    selectedCustomer?.uuid ||
    selectedMsp?.uuid ||
    selectedAccount?.uuid ||
    (routeOrgUuid as string | null) ||
    s?.state?.authentication?.selectedAccount?.uuid ||
    null
  );
};

/**
 * Build a scoped URL of the form /<orgType>/<orgUuid>/<suffix?>
 * - orgType: "system" | "msp" | "customer"
 * - orgUuid: explicit UUID; if omitted/null, falls back to selectedAccount.uuid
 * - suffix: optional trailing path (with or without leading slash)
 */
export const buildOrgUrl = (
  orgType: string,
  orgUuid?: string | null,
  suffix = "",
): string => {
  const uuid = orgUuid || currentOrgUuid(orgType);
  const trailing = cleanSuffix(suffix);
  if (!uuid) {
    // Return original suffix if we cannot build a scoped URL.
    return `/${orgType}${trailing}`;
  }
  return `/${orgType}/${uuid}${trailing}`;
};

// Test hook to override store in unit tests
export const __setMockStore = (s: typeof store | null) => {
  mockStore = s;
};

export default buildOrgUrl;
